import type {
  IFixingPrice,
  IFixingPriceHistory,
  IFixingPricePublishRequest,
  IFixingPricePublishResponse,
  IFixingPriceRecord,
  IFixingPriceRecordList,
  IFundingRateHistory,
  IFundingRatePublishRequest,
  IFundingRatePublishResponse,
  IFundingSettlement,
  IFundingSettlementInput,
  IInstrumentFixingHistory,
  IPublishedFundingSettlement,
} from "@pulse-ui/codegen-v2/generated_ts/manual_market_data";
import { api } from "../base";

export type {
  IFixingPrice,
  IFixingPriceHistory,
  IFixingPricePublishRequest,
  IFixingPricePublishResponse,
  IFixingPriceRecord,
  IFixingPriceRecordList,
  IFundingRateHistory,
  IFundingRatePublishRequest,
  IFundingRatePublishResponse,
  IFundingSettlement,
  IFundingSettlementInput,
  IInstrumentFixingHistory,
  IPublishedFundingSettlement,
};

/** Query of the EOD fixing price history; from/to are RFC3339, an omitted filter matches all. */
export interface GetEodFixingPricesParams {
  symbol?: string;
  from?: string;
  to?: string;
  limit?: number;
}

/** Prefix of the 400 body for a rate past the sanity bound; resend acknowledged to publish it. */
export const FUNDING_RATE_OUT_OF_BOUNDS_PREFIX = "out_of_bounds:";

const manualMarketDataEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** A symbol's retained funding settlements and the cadence slots that have none. */
    getFundingRates: build.query<IFundingRateHistory, string>({
      query: (symbol) => ({
        url: "/api/trading-ops/v1/funding-rates",
        params: { symbol },
      }),
      providesTags: (_result, _error, symbol) => [{ type: "FundingRates", id: symbol }],
    }),

    /** Publishes settlements onto mktdata.fundingrate; the last record for a slot wins. */
    publishFundingRates: build.mutation<IFundingRatePublishResponse, IFundingRatePublishRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/funding-rates",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, body) => [{ type: "FundingRates", id: body.symbol }],
    }),

    /** A symbol's retained daily fixings and the business dates that have none. */
    getFixingPrices: build.query<IFixingPriceHistory, string>({
      query: (symbol) => ({
        url: "/api/trading-ops/v1/fixing-prices",
        params: { symbol },
      }),
      providesTags: (_result, _error, symbol) => [{ type: "FixingPrices", id: symbol }],
    }),

    /** Fills one missing daily fixing on mktdata.stats; an existing one is a 409. */
    publishFixingPrice: build.mutation<IFixingPricePublishResponse, IFixingPricePublishRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/fixing-prices",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, body) => [
        { type: "FixingPrices", id: body.symbol },
        { type: "FixingPriceHistory", id: body.symbol },
      ],
    }),

    /** Every retained fixing for a symbol, newest first; served from the server's history cache. */
    getFixingPriceHistory: build.query<IInstrumentFixingHistory, string>({
      query: (symbol) => ({
        url: "/api/trading-ops/v1/fixing-price-history",
        params: { symbol },
      }),
      providesTags: (_result, _error, symbol) => [{ type: "FixingPriceHistory", id: symbol }],
      keepUnusedDataFor: 300,
    }),

    /** Persisted EOD fixing prices, newest fixing_ts first. */
    getEodFixingPrices: build.query<IFixingPriceRecordList, GetEodFixingPricesParams>({
      query: (params) => ({
        url: "/api/trading-ops/v1/eod-fixing-prices",
        params,
      }),
    }),
  }),
});

export const {
  useGetFundingRatesQuery,
  usePublishFundingRatesMutation,
  useGetFixingPricesQuery,
  usePublishFixingPriceMutation,
  useGetFixingPriceHistoryQuery,
  useGetEodFixingPricesQuery,
} = manualMarketDataEndpoints;
